<template>
  <div id="branches">
    <page-hero
        class="mb-1"
        :title="msg('Branches')"
        :subtitle="msg('Manage business units and branch configurations')"
        variant="branch"
        :stats="[{ icon: 'GitBranchIcon', value: (allBranches||[]).length, label: msg('total') }]"
    >
      <template #illustration>
        <svg viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M50 20v25M35 45h30M50 45v35M35 80h30" stroke="rgba(255,255,255,.3)" stroke-width="2"/>
          <circle cx="50" cy="20" r="8" fill="rgba(255,255,255,.3)"/>
          <circle cx="35" cy="45" r="6" fill="rgba(255,255,255,.25)"/>
          <circle cx="65" cy="45" r="6" fill="rgba(255,255,255,.25)"/>
          <circle cx="50" cy="80" r="6" fill="rgba(255,255,255,.2)"/>
          <circle cx="78" cy="25" r="8" fill="rgba(255,255,255,.08)"/>
          <circle cx="12" cy="85" r="10" fill="rgba(255,255,255,.06)"/>
        </svg>
      </template>
    </page-hero>

    <div class="branches-board" style="margin-bottom: 50px">
      <!-- ── Toolbar: search + real filters + new branch ── -->
      <b-card no-body class="mb-1">
        <b-card-body class="py-1">
          <div class="bb-toolbar">
            <div class="bb-search">
              <feather-icon icon="SearchIcon" size="16" class="bb-search__icon"/>
              <b-form-input
                  v-model="search"
                  debounce="150"
                  :placeholder="msg('Search by name, address, contact or email')"
                  class="bb-search__input"
              />
            </div>

            <!-- Setup status chips -->
            <div class="bb-chips">
              <button
                  v-for="opt in setupStatusOptions"
                  :key="opt.value"
                  type="button"
                  class="bb-chip"
                  :class="{ 'bb-chip--active': setupStatusFilter === opt.value }"
                  :style="setupStatusFilter === opt.value ? { borderColor: opt.color, color: opt.color, background: opt.bg } : {}"
                  @click="setupStatusFilter = opt.value"
              >
                {{ msg(opt.label) }}
                <span class="bb-chip__count">{{ opt.count }}</span>
              </button>
            </div>

            <!-- Channel manager filter -->
            <v-select
                v-if="channelOptions.length"
                v-model="channelFilter"
                :options="channelOptions"
                :placeholder="msg('All channel managers')"
                class="bb-select"
                :clearable="true"
            />

            <!-- Currency filter -->
            <v-select
                v-if="currencyOptions.length"
                v-model="currencyFilter"
                :options="currencyOptions"
                :placeholder="msg('All currencies')"
                class="bb-select bb-select--sm"
                :clearable="true"
            />

            <div class="bb-toolbar__spacer"></div>

            <b-button variant="primary" @click="onBusinessUnitSelect(0)">
              <feather-icon icon="PlusIcon" size="15" class="mr-25"/>
              <span class="text-nowrap">{{ msg('New Branch') }}</span>
            </b-button>
          </div>
        </b-card-body>
      </b-card>

      <!--Loading-->
      <div v-if="!loaded || !isLoaded" class="text-center py-5">
        <b-spinner label="Spinning"/>
        <div class="mt-1 text-muted">{{ msg('loading') }}</div>
      </div>

      <!--Empty state-->
      <b-card v-else-if="filteredBranches.length === 0" class="bb-empty text-center py-4">
        <feather-icon icon="GitBranchIcon" size="42" class="text-muted mb-1"/>
        <h5 class="mb-25">{{ msg('No branches match your filters') }}</h5>
        <p class="text-muted mb-1">{{ msg('Try adjusting search or filters') }}</p>
        <b-button variant="outline-primary" size="sm" @click="onBusinessUnitSelect(0)">
          <feather-icon icon="PlusIcon" size="14" class="mr-25"/>{{ msg('New Branch') }}
        </b-button>
      </b-card>

      <!-- ── Branch card grid ── -->
      <b-row v-else>
        <b-col v-for="branch in filteredBranches" :key="branch.id" cols="12" md="6" xl="4" class="mb-2">
          <div class="branch-card" @click="onBusinessUnitSelect(branch.id)">
            <div class="branch-card__accent" :style="{ background: setupColor(branch.id) }"></div>
            <div class="branch-card__body">
              <!-- Head: avatar + name + currency -->
              <div class="branch-card__head">
                <div class="branch-card__avatar" :style="{ color: setupColor(branch.id) }">
                  <img v-if="branch.logoLink" :src="branch.logoLink" alt=""/>
                  <span v-else>{{ (branch.name || '?').charAt(0).toUpperCase() }}</span>
                </div>
                <div class="branch-card__title">
                  <div class="branch-card__name">{{ branch.name || msg('Unnamed branch') }}</div>
                  <div class="branch-card__id">#{{ branch.id }}</div>
                </div>
                <span v-if="branch.cur" class="branch-card__cur">{{ branch.cur }}</span>
              </div>

              <!-- Info rows -->
              <div class="branch-card__info">
                <div v-if="branch.address" class="branch-card__row">
                  <feather-icon icon="MapPinIcon" size="13"/><span>{{ branch.address }}</span>
                </div>
                <div v-if="branch.contact" class="branch-card__row">
                  <feather-icon icon="UserIcon" size="13"/><span>{{ branch.contact }}</span>
                </div>
                <div v-if="branch.phone" class="branch-card__row">
                  <feather-icon icon="PhoneIcon" size="13"/><span>{{ branch.phone }}</span>
                </div>
                <div v-if="branch.email || branch.mail" class="branch-card__row">
                  <feather-icon icon="MailIcon" size="13"/><span>{{ branch.email || branch.mail }}</span>
                </div>
              </div>

              <!-- Badges -->
              <div class="branch-card__badges" v-if="branch.externalApiType || branch.managementFee != null">
                <span v-if="branch.externalApiType" class="bb-badge bb-badge--channel">
                  <feather-icon icon="LinkIcon" size="11" class="mr-25"/>{{ branch.externalApiType }}
                </span>
                <span v-if="branch.managementFee != null" class="bb-badge bb-badge--fee">
                  {{ branch.managementFee }}% {{ msg('mgmt') }}
                </span>
              </div>

              <!-- Setup progress -->
              <div class="branch-card__setup">
                <div class="branch-card__setup-head">
                  <span>{{ msg('Setup completion') }}</span>
                  <span class="branch-card__setup-pct" :style="{ color: setupColor(branch.id) }">
                    <feather-icon v-if="setupPct(branch.id) >= 100" icon="CheckCircleIcon" size="13"/>
                    <template v-else>{{ setupPct(branch.id) }}%</template>
                  </span>
                </div>
                <div class="branch-card__setup-track">
                  <div class="branch-card__setup-fill" :style="{ width: setupPct(branch.id) + '%', background: setupColor(branch.id) }"></div>
                </div>
              </div>

              <!-- Actions -->
              <div class="branch-card__actions" @click.stop>
                <b-button size="sm" variant="outline-secondary" @click="onBusinessUnitSelect(branch.id)">
                  <feather-icon icon="EditIcon" size="13" class="mr-25"/>{{ msg('Edit') }}
                </b-button>
                <b-button size="sm" :variant="setupPct(branch.id) >= 100 ? 'outline-success' : 'primary'" @click="openSetupWizard(branch.id)">
                  <feather-icon icon="ZapIcon" size="13" class="mr-25"/>{{ setupPct(branch.id) >= 100 ? msg('Review') : msg('Setup') }}
                </b-button>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!--Edit branch Modal -->
      <b-modal no-fade v-model="dialogs.branch" size="xl" ref="branch-modal" id="branch-modal" :hide-footer="true"
               :hide-header="false" no-close-on-backdrop centered>
        <branch-panel ref="businessUnitPanel" :id="selected" @submitted="onSubmitted"/>
        <div class="text-center" style="justify-content:center">
          <b-button variant="outline-primary" class="btn-dialog" @click="submitBusinessUnit"
                    :disabled="saving === false">
            {{ msg("Save") }}
          </b-button>
        </div>
      </b-modal>
    </div>

    <!--     <div class="p-1 w-100">
             <branches-settings/>
         </div>
 -->
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import BranchPanel from "../../components/panels/branches/branchPanel";
import BranchesSettings from "./BranchesSettings";
import PageTitle from "@/@core/components/page-title/PageTitle";
import PageHero from '@/components/PageHero.vue';

export default {
  name: "branches",
  components: { PageHero, BranchPanel },

  data() {
    return {
      selected: 0,
      loaded: false,
      saving: true,
      error: '',
      // Client-side filters for the card grid
      search: '',
      setupStatusFilter: 'all',   // all | incomplete | done
      channelFilter: null,
      currencyFilter: null,
      //Dialogs to show
      dialogs: {branch: false}
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('data', ['getGroupById']),
    ...mapGetters('branches', ['allBranches']),
    ...mapGetters('clients', ['groupAlias', 'branchAlias']),

    ...mapGetters('data', {allGroups: 'getGroups'}),
    ...mapGetters('branches', {
      _isLoaded: "isLoaded", dataError: "getError",
      getMail: "getMailById", allMails: "getMails",
      getKyc: "getKycById", allKyc: "getKyc",
      getOrganization: "getOrganizationById", allOrganizations: "getOrganization"
    }),
    ...mapGetters('data', {_getCountry: 'getCountry'}),
    ...mapGetters('setupWizard', ['getCompletionByBranch']),
    isLoaded() {
      return this._isLoaded;
    },
    /** Distinct channel-manager (externalApiType) values for the filter. */
    channelOptions() {
      const set = new Set();
      (this.allBranches || []).forEach(b => { if (b.externalApiType) set.add(b.externalApiType); });
      return Array.from(set).sort();
    },
    /** Distinct currencies for the filter. */
    currencyOptions() {
      const set = new Set();
      (this.allBranches || []).forEach(b => { if (b.cur) set.add(b.cur); });
      return Array.from(set).sort();
    },
    /** Setup-status chips with live counts. */
    setupStatusOptions() {
      const all = (this.allBranches || []);
      const done = all.filter(b => this.setupPct(b.id) >= 100).length;
      return [
        { value: 'all',        label: 'All',        color: '#7367F0', bg: 'rgba(115,103,240,0.12)', count: all.length },
        { value: 'incomplete', label: 'Needs setup', color: '#FF9F43', bg: 'rgba(255,159,67,0.12)', count: all.length - done },
        { value: 'done',       label: 'Complete',    color: '#28C76F', bg: 'rgba(40,199,111,0.12)', count: done },
      ];
    },
    /** The branches after applying search + status + channel + currency. */
    filteredBranches() {
      let list = (this.allBranches || []).slice();
      const q = (this.search || '').trim().toLowerCase();
      if (q) {
        list = list.filter(b => {
          return [b.name, b.address, b.contact, b.email, b.mail, String(b.id)]
              .some(v => v && String(v).toLowerCase().includes(q));
        });
      }
      if (this.setupStatusFilter === 'incomplete') {
        list = list.filter(b => this.setupPct(b.id) < 100);
      } else if (this.setupStatusFilter === 'done') {
        list = list.filter(b => this.setupPct(b.id) >= 100);
      }
      if (this.channelFilter) list = list.filter(b => b.externalApiType === this.channelFilter);
      if (this.currencyFilter) list = list.filter(b => b.cur === this.currencyFilter);
      return list;
    },
  },
  created() {
    let $this = this;
    // Load groups/org data + ALL branches (card grid shows everything,
    // filtering happens client-side).
    this.loadData().then(() => {
      $this.loaded = true;
      $this._refresh({ amountPerPage: 1000, filter: {} }).catch(() => {});
    }, (error) => $this.error = error);
    // Per-branch setup completion for the progress bars (silent).
    this.fetchAllSetupStatus();
  },
  methods: {
    ...mapActions('branches', ['loadData', 'change', 'refresh']),
    ...mapActions('setupWizard', ['fetchAllSetupStatus']),
    ...mapGetters('users', {_loadUserData: 'loadData'}),
    ...mapActions('branches', {_refresh: 'refresh'}),
    ...mapActions('branches', {_reload: 'reload'}),
    setupPct(branchId) {
      const map = this.getCompletionByBranch || {};
      const v = map[branchId] != null ? map[branchId] : map[String(branchId)];
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    },
    setupColor(branchId) {
      const p = this.setupPct(branchId);
      if (p >= 100) return '#28C76F';   // green — done
      if (p >= 70) return '#D4A96A';    // gold — almost there
      if (p >= 40) return '#FF9F43';    // amber — in progress
      return '#EA5455';                 // red — barely started
    },
    refresh() {
      this._refresh({ amountPerPage: 1000, filter: {} });
    },
    reload() {
      this._reload();
      this.refresh();
    },
    onBusinessUnitSelect(id) {
      this.selected = id;
      this.dialogs.branch = true
    },
    submitBusinessUnit() {
      this.$root.$emit('crm::submitBusinessUnit')
      this._loadUserData();
    },
    onSubmitted(ans) {
      if (ans && ans.id != null)
        this.selected = ans.id;
      if (ans && ans.status === false)
        return; // save failed — keep the modal open so the user can fix and retry
      // close the edit modal and refresh the branch cards with fresh data
      this.dialogs.branch = false;
      this.refresh();
    },
    openSetupWizard(branchId) {
      this.$router.push('/organization/branches/setup/' + branchId);
    }
  }
}
</script>

<style scoped>
/* ── Toolbar ── */
.bb-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.bb-toolbar__spacer { flex: 1 1 auto; }
.bb-search {
  position: relative;
  flex: 1 1 240px;
  min-width: 200px;
}
.bb-search__icon {
  position: absolute;
  left: 12px; top: 50%;
  transform: translateY(-50%);
  color: #b9b9c3;
  pointer-events: none;
}
.bb-search__input { padding-left: 34px; }
.bb-select { min-width: 190px; }
.bb-select.bb-select--sm { min-width: 140px; }

/* Setup status chips */
.bb-chips { display: inline-flex; gap: 6px; flex-wrap: wrap; }
.bb-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(130, 134, 139, 0.30);
  background: transparent;
  font-size: 12.5px;
  font-weight: 600;
  color: #6e6b7b;
  transition: all 0.15s ease;
  cursor: pointer;
}
.bb-chip:hover { border-color: rgba(130,134,139,0.55); }
.bb-chip__count {
  font-size: 11px;
  font-weight: 800;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(130, 134, 139, 0.18);
}
.bb-chip--active .bb-chip__count { background: rgba(255,255,255,0.25); }

/* ── Branch card ── */
.branch-card {
  position: relative;
  height: 100%;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(34, 41, 47, 0.08);
  box-shadow: 0 3px 12px rgba(34, 41, 47, 0.06);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}
.branch-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(34, 41, 47, 0.12);
  border-color: rgba(115, 103, 240, 0.35);
}
.branch-card__accent { height: 4px; width: 100%; }
.branch-card__body { padding: 16px; }

.branch-card__head { display: flex; align-items: center; gap: 12px; }
.branch-card__avatar {
  flex-shrink: 0;
  width: 46px; height: 46px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; font-weight: 800;
  overflow: hidden;
  background: rgba(115, 103, 240, 0.10);   /* static tint; text/initial colored by setup status */
}
.dark-layout .branch-card__avatar { background: rgba(255, 255, 255, 0.06); }
.branch-card__avatar img { width: 100%; height: 100%; object-fit: cover; }
.branch-card__title { flex: 1; min-width: 0; }
.branch-card__name {
  font-size: 15.5px; font-weight: 700; color: #5e5873;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.branch-card__id { font-size: 11.5px; color: #b9b9c3; font-weight: 600; }
.branch-card__cur {
  flex-shrink: 0;
  font-size: 11px; font-weight: 800;
  padding: 3px 9px;
  border-radius: 8px;
  background: rgba(115, 103, 240, 0.12);
  color: #7367F0;
}

.branch-card__info { margin: 14px 0 12px; display: flex; flex-direction: column; gap: 6px; }
.branch-card__row {
  display: flex; align-items: center; gap: 8px;
  font-size: 12.5px; color: #6e6b7b;
}
.branch-card__row svg { flex-shrink: 0; color: #b9b9c3; }
.branch-card__row span {
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.branch-card__badges { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.bb-badge {
  display: inline-flex; align-items: center;
  font-size: 10.5px; font-weight: 700;
  padding: 3px 9px; border-radius: 7px;
  text-transform: uppercase; letter-spacing: 0.3px;
}
.bb-badge--channel { background: rgba(0, 207, 232, 0.14); color: #00b8d4; }
.bb-badge--fee { background: rgba(40, 199, 111, 0.14); color: #28C76F; }
.bb-badge--group { background: rgba(115, 103, 240, 0.14); color: #7367F0; }

.branch-card__setup { margin-bottom: 14px; }
.branch-card__setup-head {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 11px; font-weight: 600; color: #b9b9c3;
  margin-bottom: 5px;
}
.branch-card__setup-pct { font-weight: 800; display: inline-flex; align-items: center; }
.branch-card__setup-track {
  height: 7px; border-radius: 999px;
  background: rgba(130, 134, 139, 0.18);
  overflow: hidden;
}
.branch-card__setup-fill { height: 100%; border-radius: 999px; transition: width 0.4s ease; }

.branch-card__actions { display: flex; gap: 8px; }
.branch-card__actions .btn { flex: 1; }

/* Dark layout */
.dark-layout .branch-card { background: #283046; border-color: rgba(255,255,255,0.06); }
.dark-layout .branch-card__name { color: #d0d2d6; }
.dark-layout .branch-card__row { color: #b4b7bd; }
.dark-layout .bb-chip { color: #b4b7bd; border-color: rgba(255,255,255,0.14); }
</style>
